import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import type { BroadcastLayout } from "../../lib/broadcastApi";
import { computeStageScale, formatScale, STAGE_SIZES } from "./stageScale";

export interface Size {
  width: number;
  height: number;
}

const windowSize = (): Size => ({
  width: typeof window === "undefined" ? 1920 : window.innerWidth,
  height: typeof window === "undefined" ? 1080 : window.innerHeight,
});

/** Window size, tracked on resize. */
export function useWindowSize(): Size {
  const [size, setSize] = useState<Size>(windowSize);
  useEffect(() => {
    const onResize = () => setSize(windowSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
}

/**
 * Size of an element (ResizeObserver, window fallback). Uses a callback ref
 * so the measurement starts when the element actually mounts (the control
 * page renders a loading branch first).
 */
export function useElementSize<T extends HTMLElement>(): [
  (node: T | null) => void,
  Size,
] {
  const [el, setEl] = useState<T | null>(null);
  const [size, setSize] = useState<Size>({ width: 960, height: 540 });
  useLayoutEffect(() => {
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0)
        setSize({ width: r.width, height: r.height });
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, size];
}

export const CHROMA_GREEN = "#00ff00";

export function stageBackground(
  stage: BroadcastLayout["stage"],
  branding?: { primary_color: string; secondary_color: string } | null,
  assets?: Record<string, string>,
): string {
  switch (stage.background) {
    case "image": {
      // A library background; no asset (or an unresolved one) stays clear.
      const url = stage.background_asset_id
        ? assets?.[stage.background_asset_id]
        : undefined;
      return url ? `url("${url}") center / cover no-repeat` : "transparent";
    }
    case "solid":
      return stage.background_color;
    case "chroma":
      return CHROMA_GREEN;
    case "branded":
      return `linear-gradient(135deg, ${branding?.primary_color ?? "#0f172a"}, ${
        branding?.secondary_color ?? "#1e293b"
      })`;
    default:
      return "transparent";
  }
}

interface StageProps {
  stage: BroadcastLayout["stage"];
  branding?: { primary_color: string; secondary_color: string } | null;
  /** Viewport (or preview container) the stage is fitted into. */
  fit: Size;
  /** asset_id -> url map for an `image` background. */
  assets?: Record<string, string>;
  /** Control preview: draw a checkerboard under a transparent stage. */
  checker?: boolean;
  children: ReactNode;
}

export default function Stage({
  stage,
  branding,
  fit,
  checker = false,
  assets,
  children,
}: StageProps) {
  const dims = STAGE_SIZES[stage.aspect] ?? STAGE_SIZES["16:9"];
  const scale = computeStageScale(stage.aspect, fit.width, fit.height);
  const left = (fit.width - dims.w * scale) / 2;
  const top = (fit.height - dims.h * scale) / 2;
  const bg = stageBackground(stage, branding, assets);
  const checkerCss =
    checker && stage.background === "transparent"
      ? "repeating-conic-gradient(#475569 0% 25%, #334155 0% 50%) 50% / 24px 24px"
      : undefined;

  return (
    <div
      data-testid="broadcast-viewport"
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        // Letterbox bars carry the stage fill (solid / branded / chroma) so
        // a window that does not match the stage aspect shows no white
        // band; transparent stays transparent. html/body/#root are never
        // painted (see useBroadcastDocument).
        // An image background belongs to the stage only; the bars stay clear.
        background: checker
          ? "#0b1120"
          : stage.background === "image"
            ? "transparent"
            : bg,
      }}
    >
      <div
        data-testid="broadcast-stage"
        data-global-text-scale={String(stage.global_text_scale)}
        data-scale={formatScale(scale)}
        data-aspect={stage.aspect}
        data-background={stage.background}
        style={{
          position: "absolute",
          left,
          top,
          width: dims.w,
          height: dims.h,
          transform: `scale(${scale})`,
          transformOrigin: "0 0",
          background: checkerCss ?? bg,
          overflow: "hidden",
          fontFamily:
            "Inter, 'Segoe UI', system-ui, -apple-system, Roboto, sans-serif",
        }}
      >
        {children}
      </div>
    </div>
  );
}
